import { PREVENTION_SETTING_KEYS } from '@kirin/api-client/contracts/policies';

/**
 * Feature-flag state that gates the visibility of individual policy settings.
 * Resolved by `PolicySettingsForm` from `useFeatureFlags` and passed in so the
 * hide-decision stays a pure, testable function.
 */
export interface PolicySettingVisibilityFlags {
  isExtensionCodeScanEnabled: boolean;
  isHookPreventionModeEnabled: boolean;
  isAiMcpToolAnalysisEnabled: boolean;
  isMcpToolResponseScanningEnabled: boolean;
  isCapabilityDetectionEnabled: boolean;
  isAiToolCallEnabled: boolean;
}

/**
 * KIR-4381 TEMPORARY: kill switch that hides the 'AI Tool Description Analysis'
 * row regardless of `ai_mcp_tool_analysis`. Flip to `false` (or delete it and
 * its use below) once an AI tool-description analyser is actually wired up
 * again. Typed `boolean` on purpose so the always-true branch below does not
 * get narrowed away by the compiler.
 */
const AI_TOOL_ANALYSIS_TEMPORARILY_HIDDEN: boolean = true;

/**
 * Legacy settings a newer detection engine replaces while its flag is on: the
 * scanner stops reading them, so they no longer affect detection (KIR-2691).
 * With capability detection on, the weighted catalog supersedes the
 * Dangerous Permissions list and the raw command-count threshold.
 *
 * Unlike a row hidden because its feature is off, a superseded setting is
 * hidden even as an alert's own control in the Edit Policy popup (KIR-4929).
 */
export function isSupersededPolicySetting(
  settingKey: string,
  flags: Pick<PolicySettingVisibilityFlags, 'isCapabilityDetectionEnabled'>
): boolean {
  return (
    flags.isCapabilityDetectionEnabled &&
    (settingKey === 'dangerousPermissions' ||
      settingKey === 'excessiveCommandsThreshold')
  );
}

/**
 * Decide whether a given policy setting row should be hidden in the settings
 * form. Settings are hidden when their gating feature flag is off, or when they
 * are prevention-related (controlled by the Off/Monitor/Enforce selector
 * instead of a standalone toggle).
 */
export function shouldHidePolicySetting(
  settingKey: string,
  policyKey: string,
  flags: PolicySettingVisibilityFlags
): boolean {
  // Hide extensionsCodeScan if feature flag is disabled
  if (
    !flags.isExtensionCodeScanEnabled &&
    settingKey === 'extensionsCodeScan'
  ) {
    return true;
  }

  // Hide preventSecretLeakage if hook prevention mode flag is disabled
  if (
    !flags.isHookPreventionModeEnabled &&
    settingKey === 'preventSecretLeakage'
  ) {
    return true;
  }

  // KIR-4381 TEMPORARY: hide 'AI Tool Description Analysis' unconditionally.
  // `ai_mcp_tool_analysis` is ON in production, so the flag rule below would
  // render the toggle — but the backend now forces the setting off (see
  // AI_TOOL_ANALYSIS_TEMPORARILY_DISABLED in policies/mcp/tools/config.ts),
  // so it would switch nothing. Deliberately layered ON TOP of the flag rule
  // rather than replacing it: delete this block to restore flag-gated
  // behaviour when the 2.0 kirin-ai API lands.
  if (AI_TOOL_ANALYSIS_TEMPORARILY_HIDDEN) {
    if (settingKey === 'enableAIToolAnalysis') {
      return true;
    }
  }

  // Hide enableAIToolAnalysis if AI MCP tool analysis flag is disabled
  if (
    !flags.isAiMcpToolAnalysisEnabled &&
    settingKey === 'enableAIToolAnalysis'
  ) {
    return true;
  }

  // KIR-3157: hide the MCP tool-response scan toggle unless its flag is on
  if (
    !flags.isMcpToolResponseScanningEnabled &&
    settingKey === 'enableToolResponseScan'
  ) {
    return true;
  }

  // KIR-2691: legacy settings the capability engine replaces.
  if (isSupersededPolicySetting(settingKey, flags)) {
    return true;
  }

  // KIR-2691: customCapabilityRules only shown when capability detection is enabled
  if (
    !flags.isCapabilityDetectionEnabled &&
    settingKey === 'customCapabilityRules'
  ) {
    return true;
  }

  // KIR-3837 / KIR-4902: selectedInfoTypes and removedInfoTypes are UI-only
  // companions to the PII types table (managed by the bespoke PiiTypesField) —
  // never standalone rows. They stay registered form fields so their values are
  // submitted, but render hidden.
  if (
    policyKey === 'pii-detection' &&
    (settingKey === 'selectedInfoTypes' || settingKey === 'removedInfoTypes')
  ) {
    return true;
  }

  // KIR-4426: hide Real-Time Tool-Call Analysis unless its flag is on
  if (!flags.isAiToolCallEnabled && settingKey === 'toolCallScanMode') {
    return true;
  }

  // Always hide prevention-related settings (controlled by Off/Monitor/Enforce selector)
  const keys = PREVENTION_SETTING_KEYS[policyKey];
  if (keys?.includes(settingKey)) {
    return true;
  }

  return false;
}
